import {Component} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {AutoScraperBackendService} from '../auto-scraper-backend.service';
import {MultipleHitsContainerWithItemsSelectors, MultipleHitsSelectors, PageSource, SelectorResult} from '../model';
import {FormControl, FormGroup} from '@angular/forms';
import {catchError} from 'rxjs/operators';
import {EMPTY} from 'rxjs';
import {environment} from '../../environments/environment';

@Component({
  selector: 'app-multiple-hits',
  templateUrl: './multiple-hits.component.html',
  styleUrls: ['./multiple-hits.component.css'],
  standalone: false
})
export class MultipleHitsComponent {
  constructor(private http: HttpClient, public data: AutoScraperBackendService) {}

  private get mh() {
    return this.data.supplier_implementation.multiple_hits;
  }

  private get authHeaders(): HttpHeaders {
    return new HttpHeaders({'auth': this.data.token});
  }

  simple_search: boolean = this.data.supplier_implementation.basic_supplier_info.simple_search;
  multi_hits_status = '';

  // ─── Form group ──────────────────────────────────────────────────────────────
  multi_hit_selector_search = new FormGroup({
    page_0_url0:    new FormControl(this.data.supplier_implementation.multiple_hits.search_items[0].elements[0].url),
    page_0_brand0:  new FormControl(this.data.supplier_implementation.multiple_hits.search_items[0].elements[0].brand),
    page_0_number0: new FormControl(this.data.supplier_implementation.multiple_hits.search_items[0].elements[0].number),

    page_0_url1:    new FormControl(this.data.supplier_implementation.multiple_hits.search_items[0].elements[1].url),
    page_0_brand1:  new FormControl(this.data.supplier_implementation.multiple_hits.search_items[0].elements[1].brand),
    page_0_number1: new FormControl(this.data.supplier_implementation.multiple_hits.search_items[0].elements[1].number),

    page_1_url0:    new FormControl(this.data.supplier_implementation.multiple_hits.search_items[1].elements[0].url),
    page_1_brand0:  new FormControl(this.data.supplier_implementation.multiple_hits.search_items[1].elements[0].brand),
    page_1_number0: new FormControl(this.data.supplier_implementation.multiple_hits.search_items[1].elements[0].number),

    page_1_url1:    new FormControl(this.data.supplier_implementation.multiple_hits.search_items[1].elements[1].url),
    page_1_brand1:  new FormControl(this.data.supplier_implementation.multiple_hits.search_items[1].elements[1].brand),
    page_1_number1: new FormControl(this.data.supplier_implementation.multiple_hits.search_items[1].elements[1].number),
  });

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  /** Returns true if value is non-empty (truthy). */
  are_values_ok(value: string): boolean {
    return Boolean(value);
  }

  /** Returns true only when every value in the array is non-empty. */
  private allPresent(values: (string | null | undefined)[]): boolean {
    return values.every(v => this.are_values_ok(v ?? ''));
  }

  /** Syncs the simple_search toggle back to the service layer. */
  onSimpleSearchChange() {
    this.data.supplier_implementation.basic_supplier_info.simple_search = this.simple_search;
  }

  // ─── Selector search ──────────────────────────────────────────────────────────

  /** Writes the current form values back into data.multiple_hits.search_items. */
  private syncFormToData() {
    const v = this.multi_hit_selector_search.value;
    const items = this.mh.search_items;

    const set = (p: number, e: number, url: string | null | undefined, brand: string | null | undefined, number: string | null | undefined) => {
      items[p].elements[e].url    = url    ?? '';
      items[p].elements[e].brand  = brand  ?? '';
      items[p].elements[e].number = number ?? '';
    };

    set(0, 0, v.page_0_url0, v.page_0_brand0, v.page_0_number0);
    set(0, 1, v.page_0_url1, v.page_0_brand1, v.page_0_number1);
    set(1, 0, v.page_1_url0, v.page_1_brand0, v.page_1_number0);
    set(1, 1, v.page_1_url1, v.page_1_brand1, v.page_1_number1);
  }

  /** Returns an error message if the selectors response is missing required fields, or null when valid. */
  private validateSelectorResponse(mhs: MultipleHitsSelectors): string | null {
    if (mhs.selectors.length === 0) return 'No selector for container was found';
    if (mhs.selectors[0].url.length === 0) return 'No selector for url was found';
    if (this.simple_search) {
      if (mhs.selectors[0].brand.length === 0) return 'No selector for brand was found';
      if (mhs.selectors[0].number.length === 0) return 'No selector for number was found';
    }
    return null;
  }

  /** Validates form inputs, POSTs to /search_identifier_for_multi_hit, and stores the returned container selectors. */
  onSubmit_multi_hit_selector_search() {
    this.syncFormToData();
    this.data.supplier_implementation.basic_supplier_info.simple_search = this.simple_search;

    const v = this.multi_hit_selector_search.value;

    if (!this.allPresent([v.page_0_url0, v.page_0_url1, v.page_1_url0, v.page_1_url1])) {
      this.multi_hits_status = 'Missing target value for url'; return;
    }
    if (!this.allPresent([this.mh.search_items[0].page_source, this.mh.search_items[1].page_source])) {
      this.multi_hits_status = 'Missing page source'; return;
    }
    if (this.simple_search) {
      if (!this.allPresent([v.page_0_brand0, v.page_0_brand1, v.page_1_brand0, v.page_1_brand1])) {
        this.multi_hits_status = 'Missing target value for brand'; return;
      }
      if (!this.allPresent([v.page_0_number0, v.page_0_number1, v.page_1_number0, v.page_1_number1])) {
        this.multi_hits_status = 'Missing target value for number'; return;
      }
    }

    this.multi_hits_status = 'Working on it';
    this.http.post(
      environment.apiUrl + '/search_identifier_for_multi_hit',
      this.data.supplier_implementation,
      {headers: this.authHeaders},
    ).pipe(
      catchError(() => { this.multi_hits_status = 'Error'; return EMPTY; })
    ).subscribe(res => {
      if (typeof res === 'string') { this.multi_hits_status = res; return; }
      const mhs = res as MultipleHitsSelectors;
      this.data.supplier_implementation.multiple_hits_selectors = mhs;
      const error = this.validateSelectorResponse(mhs);
      if (error) { this.multi_hits_status = error; return; }
      this.data.supplier_implementation.basic_supplier_info.simple_search = this.simple_search;
      this.multi_hits_status = 'Success';
    });
  }

  // ─── Selectors overlay ────────────────────────────────────────────────────────

  private readonly selectorFieldMap: Record<string, (s: MultipleHitsContainerWithItemsSelectors) => SelectorResult[]> = {
    url:    s => s.url,
    brand:  s => s.brand,
    number: s => s.number,
  };

  /** Populates and opens the selectors overlay for the given field (url, brand, or number). */
  showSelectors(value: string) {
    const mhs = this.data.supplier_implementation.multiple_hits_selectors;
    const max = 15;

    const titleEl = document.getElementById('overlay-title');
    if (titleEl) titleEl.innerText = `Selectors: ${value} (top ${max})`;

    let html = '';

    if (mhs.selectors.length > 0) {
      const container = mhs.selectors[0];

      html += `<p class="sel-section-title">container selectors</p>
      <table class="sel-modal-table mb-3">
        <thead><tr><th style="width:2.5rem">#</th><th>Selector</th></tr></thead>
        <tbody>`;
      for (let i = 0; i < container.container_selectors.length && i < 10; i++) {
        html += `<tr><td>${i}</td><td><code>${container.container_selectors[i]}</code></td></tr>`;
      }
      html += `</tbody></table>`;

      const getList = this.selectorFieldMap[value];
      if (getList) {
        const list = getList(container);
        html += `<p class="sel-section-title mt-3">selectors for ${value}</p>
        <table class="sel-modal-table">
          <thead>
            <tr>
              <th style="width:2.5rem">#</th>
              <th style="width:30%">Extracted value</th>
              <th>Selector(s)</th>
              <th style="width:8rem">Timestamp</th>
            </tr>
          </thead>
          <tbody>`;
        for (let i = 0; i < list.length && i < max; i++) {
          const sel = list[i];
          const paths = sel.selector.map(p => `<code>${p}</code>`).join('<br>');
          html += `<tr>
            <td>${i}</td>
            <td>${sel.extracted_value ?? ''}</td>
            <td>${paths}</td>
            <td>${sel.timestamp ?? ''}</td>
          </tr>`;
        }
        html += `</tbody></table>`;
      }
    } else {
      html = `<p style="color:var(--bp-muted)">No selectors available.</p>`;
    }

    document.getElementById('text')!.innerHTML = html;
    document.getElementById('overlay')!.style.display = 'block';
  }

  /** Hides the selectors overlay. */
  overlay_off() {
    document.getElementById('overlay')!.style.display = 'none';
  }
}
